import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, LoaderCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

function ShopbookLogo({ compact = false }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`rounded-2xl overflow-hidden bg-white backdrop-blur flex items-center justify-center ${
          compact ? "h-10 w-10" : "h-12 w-12"
        }`}
      >
        <img
          src="/shopbook-logo.png"
          alt="Shopbook"
          className={`block object-contain ${compact ? "h-10 w-10" : "h-11 w-11"}`}
          loading="eager"
        />
      </div>
      <div>
        <div className="font-display text-xl font-semibold text-inherit">Shopbook</div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-inherit opacity-70">Operations Console</div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login, authed } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (authed) navigate("/", { replace: true });
  }, [authed, navigate]);

  const submit = async ({ email, password }) => {
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to Shopbook Admin");
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F0F6F1" }}>
      {/* Left: brand panel */}
      <div
        className="hidden lg:flex w-1/2 relative flex-col justify-between p-12 text-white overflow-hidden"
        style={{ background: "linear-gradient(160deg, #0E5B52 0%, #0A4640 60%, #08332E 100%)" }}
      >
        <div className="relative z-10">
          <ShopbookLogo />
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight text-balance">
            Run the business of every shop, from one calm place.
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            Customers, branches, subscriptions, referrals, field agents, sync health and revenue — all visible in real time.
          </p>
          <div className="kente-divider w-40" />
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "1,284", l: "Active shops" },
              { k: "₦4.2M", l: "MRR" },
              { k: "98.7%", l: "Sync health" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-xl font-semibold">{s.k}</div>
                <div className="text-[11px] uppercase tracking-wider text-white/50 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/40">
          © {new Date().getFullYear()} ShopFlow Africa · Internal use only
        </div>

        {/* Decorative pattern */}
        <div className="absolute -right-32 -bottom-32 w-[520px] h-[520px] rounded-full opacity-[0.07]"
          style={{ background: "radial-gradient(circle, #FDE047 0%, transparent 70%)" }} />
        <div className="absolute top-1/3 right-12 w-2 h-2 rounded-full bg-[#FDE047]" />
        <div className="absolute top-1/2 right-24 w-1 h-1 rounded-full bg-[#A7F3D0]" />
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <ShopbookLogo compact />
          </div>

          <div className="inline-flex items-center gap-2 pill" style={{ background: "#D1FAE5", color: "#065F46" }}>
            <ShieldCheck className="w-3 h-3" /> Internal admin access
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#064E3B] mt-4 tracking-tight">
            Sign in to continue
          </h2>
          <p className="text-sm text-[#4B5563] mt-2">
            Use your company email. Sessions are role-scoped and audited.
          </p>

          <form onSubmit={handleSubmit(submit)} className="mt-8 space-y-6" noValidate>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
                Work email
              </label>
              <Input
                data-testid="login-email-input"
                type="email"
                placeholder="you@company.ng"
                className="h-12 rounded-xl bg-white border-[#0E5B52]/15 focus-visible:border-transparent focus-visible:ring-0 focus-visible:outline-none"
                autoComplete="email"
                aria-invalid={errors.email ? "true" : "false"}
                {...register("email")}
              />
              {errors.email && <p className="mt-1.5 text-xs text-[#B91C1C]">{errors.email.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5">
                Password
              </label>
              <Input
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                className="h-12 rounded-xl bg-white border-[#0E5B52]/15 focus-visible:border-transparent focus-visible:ring-0 focus-visible:outline-none"
                autoComplete="current-password"
                aria-invalid={errors.password ? "true" : "false"}
                {...register("password")}
              />
              {errors.password && <p className="mt-1.5 text-xs text-[#B91C1C]">{errors.password.message}</p>}
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="mt-2 inline-block text-sm font-medium text-[#0E5B52] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 rounded-md bg-[#0E5B52] hover:bg-[#0B4A43] text-white font-semibold text-sm transition-all active:scale-[0.99]"
              >
                {loading ? (
                  <>
                    <LoaderCircle className="w-4 h-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
